import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';

import { Painel } from '../../../../components/Painel';
import { LockedFeatureContent } from '../../../../components/LockedFeaturePage';
import { ReportsService } from '../../../../services/ReportsService';
import { IAdvancedReports } from '../../../../domains/Reports';
import { useAuth } from '../../../../hooks/auth';
import { isFeatureGranted } from '../../../../utils/isFeatureGranted';
import { AtRiskStudents } from '../../../dashboard/pages/dashboard/components/AtRiskStudents';

import {
  Card,
  CardTitle,
  Container,
  EmptyState,
  Grid,
  PageSubtitle,
  PageTitle,
  RankedBarFill,
  RankedBarTrack,
  RankedList,
  RankedRow,
  RankedRowHeader,
  SectionTitle,
  StatBlock,
  StatLabel,
  StatRow,
  StatValue,
  VerticalBar,
  VerticalBarColumn,
  VerticalBarLabel,
  VerticalBarValue,
  VerticalChart,
} from './styles';

const BUCKET_LABELS: Record<string, string> = {
  never: 'Nunca treinou',
  '0-3': '0-3 dias',
  '4-7': '4-7 dias',
  '8-14': '8-14 dias',
  '15-30': '15-30 dias',
  '30+': '30+ dias',
};

const formatCurrency = (value: number): string =>
  value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatPercentChange = (value: number | null): string => {
  if (value === null) return '—';

  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

const formatDuration = (seconds: number | null): string => {
  if (seconds === null) return '—';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}min`;

  return `${Math.floor(hours / 24)}d`;
};

const AdvancedReports: React.FC = () => {
  const reportsService = new ReportsService();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [report, setReport] = useState<IAdvancedReports>();

  const hasAtRiskFeature = isFeatureGranted(user?.features?.at_risk_students);

  useEffect(() => {
    reportsService
      .getAdvancedReports()
      .then(response => setReport(response.data))
      .catch(err => {
        if (err?.response?.status === 403) {
          setLocked(true);
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activityMax = useMemo(
    () =>
      Math.max(
        1,
        ...(report?.retention.activityDistribution.map(b => b.count) || [0]),
      ),
    [report],
  );

  const weeklyMax = useMemo(
    () =>
      Math.max(
        1,
        ...(report?.engagement.weeklyCompletedTrainings.map(w => w.count) || [
          0,
        ]),
      ),
    [report],
  );

  const muscleGroupsMax = useMemo(
    () =>
      Math.max(
        1,
        ...(report?.engagement.topMuscleGroups.map(i => i.count) || [0]),
      ),
    [report],
  );

  const exercisesMax = useMemo(
    () =>
      Math.max(
        1,
        ...(report?.engagement.topExercises.map(i => i.count) || [0]),
      ),
    [report],
  );

  const staffMax = useMemo(
    () =>
      Math.max(
        1,
        ...(report?.team.closedThreadsPerStaff.map(i => i.closings_count) || [
          0,
        ]),
      ),
    [report],
  );

  return (
    <Painel loading={loading}>
      <Container>
        <div>
          <PageTitle>Relatórios avançados</PageTitle>
          <PageSubtitle>
            Retenção, engajamento e operação da equipe
          </PageSubtitle>
        </div>

        {hasAtRiskFeature && (
          <section>
            <SectionTitle>Retenção de alunos</SectionTitle>
            <AtRiskStudents />
          </section>
        )}

        {locked && <LockedFeatureContent />}

        {!locked && report && (
          <>
            <section>
              <SectionTitle>Retenção</SectionTitle>
              <Grid>
                <Card>
                  <CardTitle>Dias desde o último treino</CardTitle>
                  <VerticalChart>
                    {report.retention.activityDistribution.map(bucket => (
                      <VerticalBarColumn key={bucket.bucket}>
                        <VerticalBarValue>{bucket.count}</VerticalBarValue>
                        <VerticalBar
                          height={(bucket.count / activityMax) * 100}
                        />
                        <VerticalBarLabel>
                          {BUCKET_LABELS[bucket.bucket]}
                        </VerticalBarLabel>
                      </VerticalBarColumn>
                    ))}
                  </VerticalChart>
                </Card>
              </Grid>
            </section>

            <section>
              <SectionTitle>Engajamento com treinos</SectionTitle>
              <Grid>
                <Card>
                  <CardTitle>Treinos concluídos por semana</CardTitle>
                  {report.engagement.weeklyCompletedTrainings.length === 0 ? (
                    <EmptyState>Sem treinos concluídos ainda.</EmptyState>
                  ) : (
                    <VerticalChart>
                      {report.engagement.weeklyCompletedTrainings.map(week => (
                        <VerticalBarColumn key={week.week_start}>
                          <VerticalBarValue>{week.count}</VerticalBarValue>
                          <VerticalBar
                            height={(week.count / weeklyMax) * 100}
                          />
                          <VerticalBarLabel>
                            {format(new Date(week.week_start), 'dd/MM')}
                          </VerticalBarLabel>
                        </VerticalBarColumn>
                      ))}
                    </VerticalChart>
                  )}
                </Card>

                <Card>
                  <CardTitle>Grupos musculares mais prescritos</CardTitle>
                  {report.engagement.topMuscleGroups.length === 0 ? (
                    <EmptyState>Nenhum treino prescrito ainda.</EmptyState>
                  ) : (
                    <RankedList>
                      {report.engagement.topMuscleGroups.map(item => (
                        <RankedRow key={item.name}>
                          <RankedRowHeader>
                            <span>{item.name}</span>
                            <span>{item.count}</span>
                          </RankedRowHeader>
                          <RankedBarTrack>
                            <RankedBarFill
                              percent={(item.count / muscleGroupsMax) * 100}
                            />
                          </RankedBarTrack>
                        </RankedRow>
                      ))}
                    </RankedList>
                  )}
                </Card>

                <Card>
                  <CardTitle>Exercícios mais prescritos</CardTitle>
                  {report.engagement.topExercises.length === 0 ? (
                    <EmptyState>Nenhum treino prescrito ainda.</EmptyState>
                  ) : (
                    <RankedList>
                      {report.engagement.topExercises.map(item => (
                        <RankedRow key={item.name}>
                          <RankedRowHeader>
                            <span>{item.name}</span>
                            <span>{item.count}</span>
                          </RankedRowHeader>
                          <RankedBarTrack>
                            <RankedBarFill
                              percent={(item.count / exercisesMax) * 100}
                            />
                          </RankedBarTrack>
                        </RankedRow>
                      ))}
                    </RankedList>
                  )}
                </Card>
              </Grid>
            </section>

            <section>
              <SectionTitle>Equipe</SectionTitle>
              <Grid>
                <Card>
                  <CardTitle>Resposta no atendimento</CardTitle>
                  <StatRow>
                    <StatBlock>
                      <StatValue>
                        {formatDuration(
                          report.team.chatResponse.avg_response_seconds,
                        )}
                      </StatValue>
                      <StatLabel>Tempo médio até a 1ª resposta</StatLabel>
                    </StatBlock>
                    <StatBlock>
                      <StatValue>
                        {report.team.chatResponse.closings_count}
                      </StatValue>
                      <StatLabel>Conversas respondidas</StatLabel>
                    </StatBlock>
                    <StatBlock>
                      <StatValue>
                        {report.team.chatResponse.pending_count}
                      </StatValue>
                      <StatLabel>Atendimentos abertos</StatLabel>
                    </StatBlock>
                    <StatBlock>
                      <StatValue>
                        {report.team.chatResponse.in_progress_count}
                      </StatValue>
                      <StatLabel>Atendimentos em andamento</StatLabel>
                    </StatBlock>
                  </StatRow>
                </Card>

                <Card>
                  <CardTitle>
                    Atendimentos finalizados por membro da equipe
                  </CardTitle>
                  {report.team.closedThreadsPerStaff.length === 0 ? (
                    <EmptyState>
                      Nenhum atendimento finalizado ainda.
                    </EmptyState>
                  ) : (
                    <RankedList>
                      {report.team.closedThreadsPerStaff.map(item => (
                        <RankedRow key={item.staff_user_id}>
                          <RankedRowHeader>
                            <span>{item.staff_fullname}</span>
                            <span>{item.closings_count}</span>
                          </RankedRowHeader>
                          <RankedBarTrack>
                            <RankedBarFill
                              percent={(item.closings_count / staffMax) * 100}
                            />
                          </RankedBarTrack>
                        </RankedRow>
                      ))}
                    </RankedList>
                  )}
                </Card>
              </Grid>
            </section>

            {!!report.financial && (
              <section>
                <SectionTitle>Financeiro</SectionTitle>
                <Grid>
                  <Card>
                    <CardTitle>Inadimplência</CardTitle>
                    <StatRow>
                      <StatBlock>
                        <StatValue>{report.financial.overdueCount}</StatValue>
                        <StatLabel>Alunos inadimplentes</StatLabel>
                      </StatBlock>
                    </StatRow>
                  </Card>

                  <Card>
                    <CardTitle>Mensalidades recebidas no mês</CardTitle>
                    <StatRow>
                      <StatBlock>
                        <StatValue>
                          {formatCurrency(
                            report.financial.currentMonthReceived,
                          )}
                        </StatValue>
                        <StatLabel>
                          {formatPercentChange(
                            report.financial.percentChangeVsPreviousMonth,
                          )}{' '}
                          em relação ao mês anterior (
                          {formatCurrency(
                            report.financial.previousMonthReceived,
                          )}
                          )
                        </StatLabel>
                      </StatBlock>
                    </StatRow>
                  </Card>
                </Grid>
              </section>
            )}
          </>
        )}
      </Container>
    </Painel>
  );
};

export { AdvancedReports };
